/**
 * The week, told back to the men who played it.
 *
 * Everything here is derived, never stored. The trip is over the moment the
 * last card is signed, and from that instant the tournament document plus the
 * scores contain every fact anybody could want about it — who made the most
 * birdies, whose good six holes were the best six holes anybody played, which
 * man beat the number he arrived with. Computing it means the yearbook can
 * never disagree with the board, which a snapshot written at settlement time
 * eventually would.
 *
 * The one thing it will not do is invent a winner. `champion` is null until
 * the championship is final, for the same reason the Board refuses to crown
 * anybody early: the flights are seeded off signed cards, and a yearbook that
 * names a champion three days out is worse than one that says "not yet".
 */

import { countersForPlayers } from './engine/scoring'
import type { HoleScore, Tournament } from './engine/types'
import { buildContext, inPlayOrder, playerName, type TournamentView } from './select'

/** One man's round, kept whole so the card can be drawn from it. */
export interface YearRound {
  roundId: string
  label: string
  date: string
  /** His course handicap for this round — the figure he played off. */
  courseHandicap: number
  cells: { hole: number; par: number; gross: number | null }[]
  gross: number
  par: number
  toPar: number
  /** To par once his own allowance is taken off. */
  net: number
  complete: boolean
}

/** A run of consecutive holes, good or bad. */
export interface Stretch {
  holes: number
  from: number
  to: number
  /** Shots over par across the run; negative is under. */
  toPar: number
  label: string
}

export interface PlayerYear {
  playerId: string
  name: string
  fullName: string
  handicapIndex: number
  position: number
  points: number
  cash: number
  flight: string | null
  birdies: number
  eagles: number
  /** Longest run of holes he played without a birdie. */
  drought: number
  rounds: YearRound[]
  bestNine: YearRound | null
  worstNine: YearRound | null
  bestStretch: Stretch | null
  worstStretch: Stretch | null
  /**
   * Shots against the handicaps he actually played off, across the week.
   * Positive is over — which is where almost everybody lives.
   */
  vsHandicap: number
  won: number
  lost: number
  halved: number
  /** Prize money: quota cash plus his championship flight money. */
  purse: number
  championship: YearRound | null
}

/** A single superlative, ready to render. */
export interface Award {
  key: string
  title: string
  playerId: string
  name: string
  /** The number that won it. */
  figure: string
  /** Where it happened, or what it was worth. */
  detail: string
}

export interface Yearbook {
  players: PlayerYear[]
  awards: Award[]
  /** Null until the championship has been played and signed for. */
  champion: PlayerYear | null
  /** Every man's total, for the money table. */
  purseTotal: number
}

const STRETCHES = [3, 4, 5, 6] as const

/**
 * The best and worst runs inside one card.
 *
 * Lengths three to six, because two holes is noise and seven is most of a nine.
 * Ties break toward the LONGER run: four holes at two under is a better piece
 * of golf than three holes at two under, and the man will tell you so.
 */
function stretchesOf(r: YearRound): { best: Stretch | null; worst: Stretch | null } {
  const played = r.cells.filter((c) => c.gross != null)
  let best: Stretch | null = null
  let worst: Stretch | null = null
  for (const len of STRETCHES) {
    for (let i = 0; i + len <= played.length; i++) {
      const win = played.slice(i, i + len)
      const d = win.reduce((s, c) => s + (c.gross! - c.par), 0)
      const cand: Stretch = {
        holes: len,
        from: win[0]!.hole,
        to: win[len - 1]!.hole,
        toPar: d,
        label: r.label,
      }
      if (!best || d < best.toPar || (d === best.toPar && len > best.holes)) best = cand
      if (!worst || d > worst.toPar || (d === worst.toPar && len > worst.holes)) worst = cand
    }
  }
  return { best, worst }
}

const toPar = (n: number): string => (n === 0 ? 'E' : n > 0 ? `+${n}` : `${n}`)

/**
 * Build the whole book.
 *
 * `view` is passed in rather than recomputed because every screen that wants
 * the yearbook already has one, and `computeAll` over a played week is not
 * cheap enough to run twice for the same answer.
 */
export function buildYearbook(
  doc: Tournament,
  scores: HoleScore[],
  view: TournamentView,
): Yearbook {
  const flightOf = new Map<string, string>()
  for (const f of view.flights) for (const p of f.playerIds) flightOf.set(p, f.flight.name)
  const standing = new Map(view.standings.rows.map((r) => [r.playerId, r]))

  const champMatch = doc.matches.find(
    (m) => m.config.kind === 'strokeNet' && m.config.flighted,
  )
  const champFinal = Boolean(champMatch && view.finalMatchIds.has(champMatch.id))

  const by = new Map<string, PlayerYear>()
  for (const p of doc.players.filter((x) => x.active)) {
    const row = standing.get(p.id)
    by.set(p.id, {
      playerId: p.id,
      name: p.displayName,
      fullName: p.fullName || p.displayName,
      handicapIndex: p.handicapIndex,
      position: row?.position ?? 0,
      points: row?.points ?? 0,
      cash: row?.cash ?? 0,
      flight: flightOf.get(p.id) ?? null,
      birdies: 0,
      eagles: 0,
      drought: 0,
      rounds: [],
      bestNine: null,
      worstNine: null,
      bestStretch: null,
      worstStretch: null,
      vsHandicap: 0,
      won: 0,
      lost: 0,
      halved: 0,
      purse: 0,
      championship: null,
    })
  }

  /** Rounds are visited once each, in play order, however many matches share one. */
  const seenRound = new Set<string>()

  for (const m of doc.matches) {
    const round = doc.rounds.find((r) => r.id === m.roundId)
    if (!round) continue
    const ctx = buildContext(doc, round, m, scores)
    if (!ctx) continue
    const ids = ctx.groups.flatMap((g) => g.playerIds)

    // Counters are per MATCH, because a round played twice (front and back of
    // the same nine, say) counts its birdies once per card, not once per round.
    const gross = countersForPlayers(ctx, ids, 'gross')
    for (const pid of ids) {
      const y = by.get(pid)
      if (!y) continue
      y.birdies += gross[pid]?.birdies ?? 0
      y.eagles += gross[pid]?.eagles ?? 0
    }

    if (!seenRound.has(round.id)) {
      seenRound.add(round.id)
      for (const pid of ids) {
        const y = by.get(pid)
        if (!y) continue
        const cells = ctx.holes.map((h, i) => {
          const v = ctx.scores[pid]?.[i]
          return {
            hole: h.number,
            par: h.par,
            gross: v?.state === 'holed' ? v.gross : null,
          }
        })
        const played = cells.filter((c) => c.gross != null)
        if (played.length === 0) continue

        const ch = ctx.handicaps[pid]?.courseHandicap ?? 0
        const g = played.reduce((s, c) => s + c.gross!, 0)
        const par = played.reduce((s, c) => s + c.par, 0)
        const r: YearRound = {
          roundId: round.id,
          label: round.label,
          date: round.date,
          courseHandicap: ch,
          cells,
          gross: g,
          par,
          toPar: g - par,
          net: g - par - ch,
          complete: played.length === ctx.holes.length,
        }
        y.rounds.push(r)
        y.vsHandicap += r.net
        if (champMatch && m.id === champMatch.id) y.championship = r
      }
    }

    // Head to head, for the match-play record.
    const entry = view.byMatch[m.id]
    for (const pr of doc.pairings.filter((x) => x.matchId === m.id)) {
      const a = entry?.tabs.find((t) => t.unitId === pr.sides[0]!.id)
      const b = entry?.tabs.find((t) => t.unitId === pr.sides[1]!.id)
      if (!a || !b) continue
      const winner = a.rankValue > b.rankValue ? 0 : b.rankValue > a.rankValue ? 1 : -1
      pr.sides.forEach((side, si) => {
        for (const pid of side.playerIds) {
          const y = by.get(pid)
          if (!y) continue
          if (winner === -1) y.halved += 1
          else if (winner === si) y.won += 1
          else y.lost += 1
        }
      })
    }

    for (const a of entry?.settlement?.awards ?? []) {
      const y = by.get(a.playerId)
      if (y && a.cash > 0) y.purse += a.cash
    }
  }

  /*
   * Play order, which is not alphabetical and not the order matches were
   * declared. Sorting on the label put "Bandon Dunes — back" before its own
   * front nine; sorting on nothing left the championship in the middle of
   * Tuesday. Both matter here more than they look: the cards are read as a
   * week, and the birdie drought is a run ACROSS rounds, so a mis-ordered card
   * list silently produces a mis-counted drought — which is exactly how this
   * first shipped, reporting 55 holes where the truth was 58.
   */
  const playOrder = new Map(inPlayOrder(doc.rounds).map((r, i) => [r.id, i]))
  for (const y of by.values()) {
    y.rounds.sort(
      (a, b) => (playOrder.get(a.roundId) ?? 0) - (playOrder.get(b.roundId) ?? 0),
    )
    let run = 0
    for (const r of y.rounds) {
      for (const c of r.cells) {
        if (c.gross == null) continue
        if (c.gross < c.par) run = 0
        else {
          run += 1
          if (run > y.drought) y.drought = run
        }
      }
    }
    const nines = y.rounds.filter((r) => r.complete && r.cells.length === 9)
    y.bestNine = nines.reduce<YearRound | null>(
      (best, r) => (!best || r.toPar < best.toPar ? r : best),
      null,
    )
    y.worstNine = nines.reduce<YearRound | null>(
      (worst, r) => (!worst || r.toPar > worst.toPar ? r : worst),
      null,
    )
    for (const r of y.rounds) {
      const { best, worst } = stretchesOf(r)
      if (best && (!y.bestStretch || best.toPar < y.bestStretch.toPar)) y.bestStretch = best
      if (worst && (!y.worstStretch || worst.toPar > y.worstStretch.toPar)) y.worstStretch = worst
    }
  }

  const players = [...by.values()].sort((a, b) => a.position - b.position)
  const champion = champFinal ? (players.find((p) => p.position === 1) ?? null) : null

  /** Pick the man who tops a measure, skipping anyone with nothing to measure. */
  const top = <T>(
    pick: (p: PlayerYear) => T | null,
    better: (a: T, b: T) => boolean,
  ): { p: PlayerYear; v: T } | null => {
    let out: { p: PlayerYear; v: T } | null = null
    for (const p of players) {
      const v = pick(p)
      if (v == null) continue
      if (!out || better(v, out.v)) out = { p, v }
    }
    return out
  }

  const awards: Award[] = []
  const push = (key: string, title: string, r: { p: PlayerYear; v: unknown } | null, figure: string, detail: string) => {
    if (r) awards.push({ key, title, playerId: r.p.playerId, name: r.p.name, figure, detail })
  }

  const mostBirdies = top((p) => p.birdies || null, (a, b) => a > b)
  push('birdies', 'Most birdies', mostBirdies, `${mostBirdies?.v ?? 0}`, 'gross, all week')

  const lowNine = top((p) => p.bestNine, (a, b) => a.gross < b.gross)
  push('lowNine', 'Lowest nine', lowNine, `${lowNine?.v.gross ?? ''}`, lowNine ? `${toPar(lowNine.v.toPar)} · ${lowNine.v.label}` : '')

  const lowChamp = top((p) => p.championship, (a, b) => a.gross < b.gross)
  push('lowRound', 'Low round of the championship', lowChamp, `${lowChamp?.v.gross ?? ''}`, 'gross, Bandon Trails')

  const lowNet = top((p) => p.championship, (a, b) => a.net < b.net)
  push('lowNet', 'Lowest net, championship', lowNet, lowNet ? toPar(lowNet.v.net) : '', 'the whole field')

  const bestRun = top((p) => p.bestStretch, (a, b) => a.toPar < b.toPar || (a.toPar === b.toPar && a.holes > b.holes))
  push('bestStretch', 'Best stretch', bestRun, bestRun ? toPar(bestRun.v.toPar) : '',
    bestRun ? `${bestRun.v.holes} holes, ${bestRun.v.from}–${bestRun.v.to} · ${bestRun.v.label}` : '')

  const worstRun = top((p) => p.worstStretch, (a, b) => a.toPar > b.toPar || (a.toPar === b.toPar && a.holes > b.holes))
  push('worstStretch', 'Worst stretch', worstRun, worstRun ? toPar(worstRun.v.toPar) : '',
    worstRun ? `${worstRun.v.holes} holes, ${worstRun.v.from}–${worstRun.v.to} · ${worstRun.v.label}` : '')

  // Closest to his own number over the whole week — the honest-handicap prize.
  const honest = top((p) => (p.rounds.length ? p.vsHandicap : null), (a, b) => a < b)
  push('honest', 'Closest to his handicap', honest, honest ? `+${honest.v}` : '', 'across the week')

  const drought = top((p) => p.drought || null, (a, b) => a > b)
  push('drought', 'Longest birdie drought', drought, `${drought?.v ?? 0}`, 'holes without one')

  const richest = top((p) => p.purse || null, (a, b) => a > b)
  push('purse', 'Most prize money', richest, `$${(richest?.v ?? 0).toLocaleString()}`, 'from the pot')

  const bestMatch = top((p) => (p.won + p.lost + p.halved ? p.won : null), (a, b) => a > b)
  push('matches', 'Most matches won', bestMatch, `${bestMatch?.v ?? 0}`,
    bestMatch ? `${bestMatch.p.won}–${bestMatch.p.lost}–${bestMatch.p.halved}` : '')

  return {
    players,
    awards,
    champion,
    purseTotal: players.reduce((s, p) => s + p.purse, 0),
  }
}

/** The yearbook as a message — what gets shared out of the app. */
export function yearbookText(doc: Tournament, book: Yearbook, forPlayer?: string): string {
  const lines: string[] = [doc.name]
  if (book.champion) {
    lines.push(`Champion: ${book.champion.fullName} — ${book.champion.points} pts`)
  }
  const p = forPlayer ? book.players.find((x) => x.playerId === forPlayer) : null
  if (p) {
    lines.push('')
    lines.push(`${p.name} — finished ${p.position} of ${book.players.length}`)
    lines.push(`${p.birdies} birdies · ${p.won}-${p.lost}-${p.halved} in matches · $${p.purse} won`)
    if (p.bestNine) lines.push(`Best nine: ${p.bestNine.gross} (${toPar(p.bestNine.toPar)}) at ${p.bestNine.label}`)
    if (p.bestStretch)
      lines.push(
        `Best stretch: ${p.bestStretch.holes} holes at ${toPar(p.bestStretch.toPar)} — ${p.bestStretch.label}`,
      )
  }
  lines.push('')
  for (const a of book.awards) lines.push(`${a.title}: ${a.name}, ${a.figure}`)
  return lines.join('\n')
}

/** The name to print, preferring the full one the yearbook is formal about. */
export const yearbookName = (doc: Tournament, id: string): string =>
  doc.players.find((p) => p.id === id)?.fullName || playerName(doc, id)
